<admin-page-simple-content
    [pageError]="pageError"
    [pageStatus]="pageStatus"
    [title]="'Photo'"
    [icon]="'photo'"
>
    <div class="content">
        @if (canManagePhoto()) {
            <div class="actions">
                <h2>Actions</h2>
                <div class="actions-buttons">
                    <admin-save-button [pageStatus]="pageStatus" [useContent]="true" (save)="reEncodeImage(photoUrn)">
                        <mat-icon>restart_alt</mat-icon>
                        Re-Encode image
                    </admin-save-button>
                    @if (photoUrn.indexOf(":external") !== -1) {
                        <admin-save-button [pageStatus]="pageStatus" [useContent]="true"
                                           (save)="invalidateImageCache(photoUrn)">
                            <mat-icon>delete</mat-icon>
                            Invalidate cache
                        </admin-save-button>
                    }
                    @if (photoInfo && photoUrn.indexOf(":upload") !== -1) {
                        <button mat-stroked-button color="primary" (click)="displayReplaceOriginalImageForm()">
                            <mat-icon>upload</mat-icon>
                            Replace Image
                        </button>
                    }
                    @if (canViewApplicationInsights()) {
                        @if (photoInfo; as photoInfo) {
                            <button mat-stroked-button color="primary" (click)="openLog(photoInfo.openLogsUrl)">
                                <mat-icon>history</mat-icon>
                                View Log
                            </button>
                        }
                    }
                </div>
            </div>
        }

        @if (photoInfo) {
            @if (showUploadForm) {
                <mat-card class="upload-form">
                    <mat-card-header>
                        <mat-card-title><h2>Replace Image</h2></mat-card-title>
                    </mat-card-header>
                    <mat-card-content class="upload-form-content">
                        <p>
                            This will replace the image with the one you upload.
                        </p>
                        <p>
                            The image will be backup and can be restored later.
                        </p>
                        <div class="upload-form-field">
                            <mat-form-field>
                                <mat-label>Version</mat-label>
                                <mat-select [(ngModel)]="versionToReplace">
                                    <mat-option [value]="'original'">
                                        Original Image
                                    </mat-option>
                                    @for (version of photoInfo.versions; track $index) {
                                        <mat-option [value]="version.width + 'x' + version.height">
                                            {{ version.width }} x {{ version.height }}
                                        </mat-option>
                                    })
                                </mat-select>
                            </mat-form-field>
                            <input #fileInput type="file"
                                   (change)="incFileSelected()"
                                   accept="image/heif,image/heic,image/png,image/jpeg,image/webp,image/tiff,image/gif,image/bmp" />
                            @if (fileInput.files && fileInput.files.length > 0) {
                                <button mat-stroked-button color="primary" [disabled]="replacingImage"
                                        (click)="uploadImage(fileInput.files)">
                                    <mat-icon>upload</mat-icon>
                                    Upload
                                </button>
                                @if (replacingImage) {
                                    <mat-spinner [diameter]="16"></mat-spinner>
                                }
                            }
                        </div>
                    </mat-card-content>
                    <mat-card-actions align="end">
                        <button mat-button (click)="showUploadForm=false">Close</button>
                    </mat-card-actions>
                </mat-card>
            }
        }
        <div class="photo-info">
            <h2>Information</h2>
            @if (photoInfo) {
                <div class="photo-thumbnail">
                    <img src="{{ photoInfo.thumbnail }}" loading="lazy" alt="Photo Thumbnail" height="100"
                         width="100" />
                </div>
            }

            <div class="field">
                <span class="label">URN</span>
                <span><code adminCopyToClipboard>{{ photoUrn }}</code></span>
            </div>
            @if (photoInfo; as photoInfo) {
                @if (photoInfo.originalFilename) {
                    <div class="field">
                        <span class="label">Original Name</span>
                        <span><code adminCopyToClipboard>{{ photoInfo.originalFilename }}</code></span>
                    </div>
                }
                <div class="field">
                    <span class="label">Dimensions</span>
                    <span class="number-info">{{ photoInfo.width }} x {{ photoInfo.height }}</span>
                </div>
                @if (photoInfo.ownerId !== '00000000-0000-0000-0000-000000000000') {
                    <div class="field">
                        <span class="label">Owner</span>
                        <span><a [routerLink]="['/users', photoInfo.ownerId]">{{ photoInfo.ownerId }}</a></span>
                    </div>
                }
                <div class="field">
                    <span class="label">Uploaded</span>
                    <span>{{ photoInfo.dateUploadedUtc | date:'medium' }} ({{ photoInfo.dateUploadedUtc | fromNow }}
                        )</span>
                </div>
                @if (photoInfo.dateTakenUtc) {
                    <div class="field">
                        <span class="label">dateTakenUtc</span>
                        <span>{{ photoInfo.dateTakenUtc | date:'medium' }}</span>
                    </div>
                }
                <div class="field">
                    <span class="label">Is Cached</span>
                    <span>{{ photoInfo.isCached }}</span>
                </div>
                @if (photoInfo.isCacheable) {
                    <div class="field">
                        <span class="label">isCacheable</span>
                        <span>{{ photoInfo.isCacheable }}</span>
                    </div>
                }
                <div class="photo-versions">
                    <div class="field">
                        <span class="label">Print</span>
                        @if (photoInfo.print) {
                            <span class="download-link">
                                <a target="_blank" [href]="photoInfo.print">View</a>
                                <a href="#" (click)="downloadFile($event, photoInfo.print)">Download</a>
                            </span>
                        } @else {
                            <span>Print version not available</span>
                        }
                    </div>
                    <div class="field">
                        <span class="label">Version</span>
                        <ul>
                            @for (version of photoInfo.versions; track $index) {
                                <li class="number-info">
                                    <div class="download-link">
                                        {{ version.width }} x {{ version.height }}
                                        <a target="_blank" [href]="version.url">View</a>
                                        <a href="#" (click)="downloadFile($event, version.url)">Download</a>
                                    </div>
                                </li>
                            }
                        </ul>
                    </div>

                    @if (photoInfo.availableBackupVersions.length > 0) {
                        <div class="field">
                            <span class="label">Available Backups</span>
                            <ul>
                                @for (backupVersion of photoInfo.availableBackupVersions; track $index) {
                                    <li>
                                        <div class="download-link">
                                            {{ backupVersion.version }}
                                            <a target="_blank" [href]="backupVersion.url">View</a>
                                            <a href="#" (click)="downloadFile($event, backupVersion.url)">Download</a>
                                        </div>
                                    </li>
                                }
                            </ul>
                        </div>
                    }</div>

                <div class="field">
                    <span class="label">Metadata</span>
                    <dl>
                        @for (metadata of photoInfo.metadata; track $index) {
                            <dt>{{ metadata.name }}</dt>
                            <dd>{{ metadata.value }}</dd>
                        }
                    </dl>
                </div>

                <div class="field">
                    <span class="label">
                      <div>Auto Crops</div>
                      <div>
                        <button mat-stroked-button (click)="drawAutoCrops(canva, photoInfo!)">Preview</button>
                      </div>
                    </span>
                    <ul>
                        @for (autoCrop of photoInfo.autoCrops; track $index) {
                            <li>
                                <div class="auto-crop-info">
                                    <span>{{ autoCrop.x }} x {{ autoCrop.y }}</span> <span>{{ autoCrop.width }}
                                    x {{ autoCrop.height }}</span>
                                    @if (showAutoCrops) {
                                        <span [style.background-color]="colors[$index]"></span>
                                    }
                                </div>
                            </li>
                        }
                    </ul>
                    <div [hidden]="!showAutoCrops">
                        <canvas width="500" height="500" #canva></canvas>
                    </div>
                </div>
            }
        </div>
        @if (pageStatus === "saveError" && pageError) {
            <admin-error [error]="pageError"></admin-error>
        }
    </div>
</admin-page-simple-content>
